// ========================================
// INPUT VALIDATION UTILITIES
// Comprehensive validation and sanitization functions
// ========================================

/**
 * Validation result type
 * @typedef {Object} ValidationResult
 * @property {boolean} isValid - Whether validation passed
 * @property {string|null} error - Error message if invalid
 */

// ========================================
// SANITIZATION
// ========================================

/**
 * Sanitize string input by removing potentially dangerous characters
 * @param {string} str - Input string
 * @returns {string} Sanitized string
 */
export function sanitizeString(str) {
  if (typeof str !== 'string') return '';
  return str
    .replace(/[<>]/g, '') // Remove angle brackets (XSS prevention)
    .replace(/javascript:/gi, '') // Remove javascript: protocol
    .replace(/on\w+=/gi, '') // Remove event handlers
    .trim();
}

/**
 * Sanitize HTML content more aggressively
 * Removes all HTML tags and dangerous patterns
 * @param {string} str - Input string
 * @returns {string} Sanitized string
 */
export function sanitizeHtml(str) {
  if (typeof str !== 'string') return '';
  return str
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '') // Remove scripts
    .replace(/<[^>]*>/g, '') // Remove all HTML tags
    .replace(/javascript:/gi, '')
    .replace(/on\w+\s*=/gi, '')
    .replace(/data:/gi, '')
    .trim();
}

/**
 * Escape HTML entities for safe display
 * @param {string} str - Input string
 * @returns {string} Escaped string
 */
export function escapeHtml(str) {
  if (typeof str !== 'string') return '';
  const escapeMap = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '`': '&#x60;',
  };
  return str.replace(/[&<>"'`]/g, char => escapeMap[char]);
}

/**
 * Sanitize object recursively
 * @param {Object} obj - Object to sanitize
 * @returns {Object} Sanitized object
 */
export function sanitizeObject(obj) {
  if (obj === null || obj === undefined) return obj;
  if (typeof obj === 'string') return sanitizeString(obj);
  if (Array.isArray(obj)) return obj.map(sanitizeObject);
  if (typeof obj === 'object') {
    const result = {};
    for (const [key, value] of Object.entries(obj)) {
      result[sanitizeString(key)] = sanitizeObject(value);
    }
    return result;
  }
  return obj;
}

// ========================================
// EMAIL VALIDATION
// ========================================

const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

/**
 * Validate email address
 * @param {string} email - Email to validate
 * @returns {ValidationResult}
 */
export function validateEmail(email) {
  if (!email || typeof email !== 'string') {
    return { isValid: false, error: 'Email is required' };
  }
  
  const trimmed = email.trim();
  
  if (trimmed.length === 0) {
    return { isValid: false, error: 'Email is required' };
  }
  
  if (trimmed.length > 254) {
    return { isValid: false, error: 'Email is too long' };
  }
  
  if (!EMAIL_REGEX.test(trimmed)) {
    return { isValid: false, error: 'Please enter a valid email address' };
  }
  
  return { isValid: true, error: null };
}

// ========================================
// PASSWORD VALIDATION
// ========================================

/**
 * Password validation options
 * @typedef {Object} PasswordOptions
 * @property {number} minLength - Minimum length (default: 8)
 * @property {boolean} requireUppercase - Require uppercase letter (default: true)
 * @property {boolean} requireLowercase - Require lowercase letter (default: true)
 * @property {boolean} requireNumber - Require number (default: true)
 * @property {boolean} requireSpecial - Require special character (default: false)
 */

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @param {PasswordOptions} options - Validation options
 * @returns {ValidationResult}
 */
export function validatePassword(password, options = {}) {
  const {
    minLength = 8,
    requireUppercase = true,
    requireLowercase = true,
    requireNumber = true,
    requireSpecial = false,
  } = options;

  if (!password || typeof password !== 'string') {
    return { isValid: false, error: 'Password is required' };
  }

  if (password.length < minLength) {
    return { isValid: false, error: `Password must be at least ${minLength} characters` };
  }

  if (password.length > 128) {
    return { isValid: false, error: 'Password is too long' };
  }

  if (requireUppercase && !/[A-Z]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one uppercase letter' };
  }

  if (requireLowercase && !/[a-z]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one lowercase letter' };
  }

  if (requireNumber && !/\d/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one number' };
  }

  if (requireSpecial && !/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
    return { isValid: false, error: 'Password must contain at least one special character' };
  }

  return { isValid: true, error: null };
}

/**
 * Get password strength score (0-4)
 * @param {string} password - Password to evaluate
 * @returns {{score: number, label: string}}
 */
export function getPasswordStrength(password) {
  if (!password) return { score: 0, label: 'Very Weak' };
  
  let score = 0;
  
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  if (/[A-Z]/.test(password) && /[a-z]/.test(password)) score++;
  if (/\d/.test(password)) score++;
  if (/[!@#$%^&*(),.?":{}|<>]/.test(password)) score++;
  
  const labels = ['Very Weak', 'Weak', 'Fair', 'Strong', 'Very Strong'];
  return { score: Math.min(score, 4), label: labels[Math.min(score, 4)] };
}

// ========================================
// NAME VALIDATION
// ========================================

/**
 * Validate a name field (first name, last name, etc.)
 * @param {string} name - Name to validate
 * @param {string} fieldName - Field name for error messages
 * @param {Object} options - Validation options
 * @returns {ValidationResult}
 */
export function validateName(name, fieldName = 'Name', options = {}) {
  const { minLength = 1, maxLength = 100, allowNumbers = false } = options;

  if (!name || typeof name !== 'string') {
    return { isValid: false, error: `${fieldName} is required` };
  }

  const trimmed = name.trim();

  if (trimmed.length < minLength) {
    return { isValid: false, error: `${fieldName} is required` };
  }

  if (trimmed.length > maxLength) {
    return { isValid: false, error: `${fieldName} is too long` };
  }

  if (!allowNumbers && /\d/.test(trimmed)) {
    return { isValid: false, error: `${fieldName} should not contain numbers` };
  }

  // Check for suspicious patterns
  if (/<|>|script|javascript/i.test(trimmed)) {
    return { isValid: false, error: `${fieldName} contains invalid characters` };
  }

  return { isValid: true, error: null };
}

// ========================================
// NUMBER VALIDATION
// ========================================

/**
 * Validate a number field
 * @param {string|number} value - Value to validate
 * @param {string} fieldName - Field name for error messages
 * @param {Object} options - Validation options
 * @returns {ValidationResult}
 */
export function validateNumber(value, fieldName = 'Value', options = {}) {
  const { min, max, integer = false, required = true } = options;

  if (value === '' || value === null || value === undefined) {
    if (required) {
      return { isValid: false, error: `${fieldName} is required` };
    }
    return { isValid: true, error: null };
  }

  const num = Number(value);

  if (isNaN(num)) {
    return { isValid: false, error: `${fieldName} must be a valid number` };
  }

  if (integer && !Number.isInteger(num)) {
    return { isValid: false, error: `${fieldName} must be a whole number` };
  }

  if (min !== undefined && num < min) {
    return { isValid: false, error: `${fieldName} must be at least ${min}` };
  }

  if (max !== undefined && num > max) {
    return { isValid: false, error: `${fieldName} must be at most ${max}` };
  }

  return { isValid: true, error: null };
}

// ========================================
// TEXT VALIDATION
// ========================================

/**
 * Validate a text field with length constraints
 * @param {string} text - Text to validate
 * @param {string} fieldName - Field name for error messages
 * @param {Object} options - Validation options
 * @returns {ValidationResult}
 */
export function validateText(text, fieldName = 'Text', options = {}) {
  const { minLength = 0, maxLength = 10000, required = true } = options;

  if (!text || typeof text !== 'string') {
    if (required) {
      return { isValid: false, error: `${fieldName} is required` };
    }
    return { isValid: true, error: null };
  }

  const trimmed = text.trim();

  if (required && trimmed.length === 0) {
    return { isValid: false, error: `${fieldName} is required` };
  }

  if (trimmed.length < minLength) {
    return { isValid: false, error: `${fieldName} must be at least ${minLength} characters` };
  }

  if (trimmed.length > maxLength) {
    return { isValid: false, error: `${fieldName} must be at most ${maxLength} characters` };
  }

  return { isValid: true, error: null };
}

// ========================================
// FORM VALIDATION
// ========================================

/**
 * Validate multiple fields at once
 * @param {Object} validations - Object with field names as keys and validation results as values
 * @returns {{isValid: boolean, errors: Object}}
 */
export function validateForm(validations) {
  const errors = {};
  let isValid = true;

  for (const [field, result] of Object.entries(validations)) {
    if (!result.isValid) {
      isValid = false;
      errors[field] = result.error;
    }
  }

  return { isValid, errors };
}

// ========================================
// QUESTION/CONTENT VALIDATION (for ImportPapers)
// ========================================

/**
 * Normalize correct answer(s) to array format
 * Supports both single answer (string) and multiple answers (array, comma-separated string)
 * @param {string|string[]|null} answer - Answer(s) to normalize
 * @returns {string[]} Array of valid answer letters (A, B, C, D)
 */
export function normalizeAnswers(answer) {
  if (!answer) return [];
  
  if (Array.isArray(answer)) {
    // Already an array: validate and normalize each item
    return answer
      .map(a => String(a).trim().toUpperCase().charAt(0))
      .filter(a => ['A', 'B', 'C', 'D'].includes(a));
  }
  
  // If string, check if it contains multiple answers (e.g., "A,C" or "A, C")
  const s = String(answer).trim().toUpperCase();
  if (s.includes(',')) {
    return s
      .split(',')
      .map(a => a.trim().charAt(0))
      .filter(a => ['A', 'B', 'C', 'D'].includes(a));
  }
  
  // Single answer - return as array for consistency
  const single = s.charAt(0);
  return (single && ['A', 'B', 'C', 'D'].includes(single)) ? [single] : [];
}

/**
 * Check if answers represent multiple correct options
 * @param {string|string[]|null} answer - Answer(s) to check
 * @returns {boolean} True if multiple answers are present
 */
export function hasMultipleAnswers(answer) {
  const answers = normalizeAnswers(answer);
  return answers.length > 1;
}

/**
 * Convert answers back to user-friendly display format
 * @param {string|string[]} answers - Answer(s) to format
 * @returns {string} Formatted answer string (e.g., "A, B" or "C")
 */
export function formatAnswers(answers) {
  const arr = Array.isArray(answers) ? answers : (answers ? [answers] : []);
  return arr
    .map(a => String(a).toUpperCase().charAt(0))
    .filter(a => ['A', 'B', 'C', 'D'].includes(a))
    .join(', ');
}

/**
 * Sanitize question content for safe display
 * Supports both single answer (string) and multiple answers (array)
 * @param {Object} question - Question object
 * @returns {Object} Sanitized question
 */
export function sanitizeQuestion(question) {
  if (!question || typeof question !== 'object') return question;

  // Normalize correctAnswer: support both single (string) and multiple (array) formats
  const sanitizedAnswer = normalizeAnswers(question.correctAnswer);

  return {
    ...question,
    id: sanitizeString(String(question.id || '')),
    text: sanitizeHtml(question.text || ''),
    options: question.options
      ? Object.fromEntries(
          Object.entries(question.options).map(([key, value]) => [
            sanitizeString(key),
            sanitizeHtml(value),
          ])
        )
      : {},
    explanation: sanitizeHtml(question.explanation || ''),
    correctAnswer: sanitizedAnswer,
    allowMultipleAnswers: Boolean(question.allowMultipleAnswers) || sanitizedAnswer.length > 1,
    tags: Array.isArray(question.tags) 
      ? question.tags.map(tag => sanitizeString(String(tag)))
      : [],
  };
}

/**
 * Validate question structure
 * Handles both single answer (string) and multiple answers (array)
 * @param {Object} question - Question to validate
 * @param {number} index - Question index for error messages
 * @returns {ValidationResult & {warnings: string[]}}
 */
export function validateQuestion(question, index = 0) {
  const warnings = [];
  const qNum = index + 1;

  if (!question || typeof question !== 'object') {
    return { isValid: false, error: `Question ${qNum} is invalid`, warnings };
  }

  if (!question.text?.trim()) {
    return { isValid: false, error: `Question ${qNum} is missing question text`, warnings };
  }

  if (!question.options || typeof question.options !== 'object') {
    return { isValid: false, error: `Question ${qNum} is missing options`, warnings };
  }

  const requiredOptions = ['A', 'B', 'C', 'D'];
  for (const opt of requiredOptions) {
    if (!question.options[opt]?.trim()) {
      return { isValid: false, error: `Question ${qNum} is missing option ${opt}`, warnings };
    }
  }

  // Handle both single answer (string) and multiple answers (array)
  let answers = [];
  if (Array.isArray(question.correctAnswer)) {
    answers = question.correctAnswer;
  } else if (question.correctAnswer) {
    // Single answer: expect a string like 'A', 'B', 'C', or 'D'
    answers = [String(question.correctAnswer).toUpperCase().charAt(0)];
  }

  if (answers.length === 0) {
    return { isValid: false, error: `Question ${qNum} has no correct answer selected`, warnings };
  }

  // Validate all answers are valid options
  const invalidAnswers = answers.filter(a => !requiredOptions.includes(String(a).toUpperCase().charAt(0)));
  if (invalidAnswers.length > 0) {
    return { isValid: false, error: `Question ${qNum} has invalid answer options: ${invalidAnswers.join(', ')}`, warnings };
  }

  // Multiple answers validation
  if (answers.length > 1 && !question.allowMultipleAnswers) {
    warnings.push(`Question ${qNum} has multiple answers but allowMultipleAnswers is not enabled`);
  }

  // Warnings (non-blocking)
  if (!question.explanation?.trim()) {
    warnings.push(`Question ${qNum} is missing explanation`);
  }

  return { isValid: true, error: null, warnings };
}

/**
 * Validate an array of questions
 * @param {Array} questions - Questions to validate
 * @returns {{isValid: boolean, errors: string[], warnings: string[]}}
 */
export function validateQuestions(questions) {
  if (!Array.isArray(questions)) {
    return { isValid: false, errors: ['Questions must be an array'], warnings: [] };
  }

  if (questions.length === 0) {
    return { isValid: false, errors: ['At least one question is required'], warnings: [] };
  }

  const errors = [];
  const warnings = [];

  questions.forEach((q, idx) => {
    const result = validateQuestion(q, idx);
    if (!result.isValid) {
      errors.push(result.error);
    }
    if (result.warnings && result.warnings.length > 0) {
      warnings.push(...result.warnings);
    }
  });

  return { isValid: errors.length === 0, errors, warnings };
}
